import { useEffect, useCallback, useRef } from "react";

import { Grid } from "@mui/material";
import { Emptyable } from "@simplyk/common";

import { StrongAuthModalSubmitData } from "../../../../common/contexts/StrongAuthContext";

import { getGoogleStytchUrl } from "@/common/helpers/stytch";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import GoogleIcon from "@/icons/illustration/google.svg";

export interface GoogleSsoContentProps {
  isSubmitting: boolean;
  error: Emptyable<string>;
  onSubmit: (data: StrongAuthModalSubmitData) => Promise<{ error: { code: string; message?: string } } | null>;
  setIsSubmitting: (value: boolean) => void;
  setError: (value: Emptyable<string>) => void;
}

export const GoogleSsoContent = ({
  isSubmitting,
  error,
  onSubmit,
  setIsSubmitting,
  setError,
}: GoogleSsoContentProps) => {
  const { t } = useTranslate();
  // Use ref to prevent processing the same token twice (race condition protection)
  const processedTokenRef = useRef<string | null>(null);
  // Use ref to store the handler so we can properly remove it
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);

  const handleMessage = useCallback(
    async (event: MessageEvent<{ type: string; token: string }>) => {
      if (event.data?.type === "STYTCH_GOOGLE_TOKEN") {
        const { token } = event.data;

        // Guard: prevent processing the same token twice
        if (processedTokenRef.current === token) {
          return;
        }
        processedTokenRef.current = token;

        setIsSubmitting(true);
        setError(undefined);

        try {
          const result = await onSubmit({ googleSsoToken: token });

          if (result?.error) {
            setError(t("common", "password.errorInvalidPassword"));
            setIsSubmitting(false);
            // Reset the processed token so user can retry
            processedTokenRef.current = null;
          }
        } catch {
          setError(t("common", "password.errorInvalidPassword"));
          setIsSubmitting(false);
          // Reset the processed token so user can retry
          processedTokenRef.current = null;
        }
      }
    },
    [onSubmit, t, setIsSubmitting, setError]
  );

  useEffect(() => {
    return () => {
      if (messageHandlerRef.current) {
        window.removeEventListener("message", messageHandlerRef.current);
        messageHandlerRef.current = null;
      }
    };
  }, []);

  const handleGoogleClick = () => {
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    window.open(
      getGoogleStytchUrl(`${window.location.origin}/auth/callback`),
      "GoogleSSO",
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Remove any existing listener before adding a new one (prevents duplicate listeners on double-click)
    if (messageHandlerRef.current) {
      window.removeEventListener("message", messageHandlerRef.current);
    }
    messageHandlerRef.current = handleMessage;
    window.addEventListener("message", handleMessage);
  };

  return (
    <>
      <Grid item>
        <DashboardTypography variant="body2">{t("common", "password.description.sso")}</DashboardTypography>
      </Grid>

      <Grid item sx={{ width: "100%" }}>
        <DashboardButton
          fullWidth
          variant="outlined"
          vibe="neutral"
          size="large"
          startIcon={<GoogleIcon />}
          onClick={handleGoogleClick}
          isLoading={isSubmitting}
          disabled={isSubmitting}
        >
          {t("dashboard", "verify.withGoogle")}
        </DashboardButton>
      </Grid>

      {error && (
        <Grid item sx={{ width: "100%" }}>
          <DashboardTypography variant="body2" sx={{ color: (theme) => theme.palette.error.main }}>
            {error}
          </DashboardTypography>
        </Grid>
      )}
    </>
  );
};
